<template lang="pug">
.box
    v-server-table(
        :url='""',
        :name='tableName',
        ref='datatable',
        :columns='columns',
        :options='options'
    )
        template(slot='beforeTable')
            datatable-loader(:name='tableName')

        template(slot='beforeFilter')
            .m-l-sm.float-right.mode-filter.width-180
                multiselect(
                    v-model='selectedPayments',
                    :options='mapPaymentList',
                    :multiple='true',
                    :close-on-select='false',
                    :clear-on-select='false',
                    placeholder='All Payments',
                    label='name',
                    track-by='id',
                    selectLabel='',
                    deselectLabel='Remove',
                    @search-change='refreshPaymentList',
                    @close='setPaymentFilter',
                    :loading='paymentLoading'
                )
                    template(
                        slot='selection',
                        slot-scope='{ values, search, isOpen }'
                    )
                        span.multiselect__single(
                            v-if='values.length && !isOpen'
                        )
                            | {{ values.length }} payments selected

                        span.multiselect__single.hide(v-else)

        template(slot='afterTable')
            es-table-pagination(
                ref='nav',
                :count='count',
                :totalRecords='totalRecords',
                :isLoading='isLoading',
                @limit-change='setLimit',
                @navigate='setAfterKey'
            )

    receipt(ref='receipt')
</template>
<script>
import { mapState } from 'vuex';
import receiptMixin from '../../../mixins/receipt';
import paymentMixin from '@js/mixins/payments';
import tillFilterMixin from '../../../components/till-filter/till-filter-mixin';
import SubscriptionMixin from '@js/mixins/subscription';

const TableName = 'VUETABLE_TRANSACTION_ES';

export default {
    mixins: [receiptMixin, paymentMixin, tillFilterMixin, SubscriptionMixin],

    data() {
        var that = this;
        let actionButtons = [];

        actionButtons.push({
            title: 'Export Details',
            action: [
                {
                    title: 'CSV',
                    callback: () => {
                        this.downloadExportDetails('CSV');
                    },
                },
                {
                    title: 'XLSX',
                    callback: () => {
                        this.downloadExportDetails('XLSX');
                    },
                },
                {
                    title: 'PDF',
                    callback: () => {
                        this.downloadExportDetails('PDF');
                    },
                },
            ],
        });

        return {
            transaction: null,
            tableName: TableName,
            afterKey: null,
            count: 0,
            isLoading: true,
            totalRecords: 0,

            columns: [
                'settled_date',
                'sequence_number',
                'receipt_type',
                'terminal_type',
                'card_type',
                'approval_code',
                'cashier',
                'outlet_name',
                'payment_name',
                'amount',
                'balance',
                'total_amount',
            ],
            options: {
                responseAdapter: resp => {
                    that.totalRecords = resp.data.count;
                    return resp.data;
                },
                requestFunction: async data => {
                    if (data.afterKey && data.afterKey === this.afterKey) {
                        data.afterKey = null;
                        this.resetPagination();
                    }

                    try {
                        const response = await axios.get(
                            Laroute.route(
                                'franchise.report.transaction.es.transactionList',
                            ),
                            {
                                params: data,
                            },
                        );

                        this.$refs.nav.afterKey = response.data.afterKey;

                        return response;
                    } catch (e) {
                        this.dispatch('error', e);
                    }
                },

                texts: {
                    title: 'Transaction List',
                    description: 'Shows transaction details',
                },

                customFilters: ['outlet_id', 'since', 'until', 'filters'],
                useReportFilter: true,

                initFilters: {
                    outlet_id: that.$store.getters.activeOutlet,
                    since: that.$store.getters.startDate.format(),
                    until: that.$store.getters.endDate.format(),
                },
                sortable: ['settled_date'],
                headings: {
                    settled_date: 'Receipt Date',
                    sequence_number: 'Receipt No.',
                    terminal_type: 'Terminal',
                    payment_name: 'Payment',
                    outlet_name: this.trans('general.outlet'),
                    total_amount: 'Grand Total (RM)',
                    amount: 'Tendered Amount (RM)',
                    balance: 'Change (RM)',
                    approval_code: 'Approval Code',
                    receipt_type: 'Receipt Type',
                    card_type: 'Card Type',
                },

                orderBy: {
                    column: 'settled_date',
                    ascending: 0,
                },

                templates: {
                    sequence_number: (h, row) => {
                        return this.receiptNumberFormat(row);
                    },

                    settled_date: (h, row) => {
                        return this.receiptDateFormat(row.settled_date);
                    },

                    terminal_type: (h, row) => {
                        return row.terminal_type === 'IPT' ||
                            row.terminal_type === 'OPT'
                            ? `${row.terminal_type} #${row.terminal_id}`
                            : '';
                    },

                    approval_code: (h, row) => {
                        const approval_code =
                            row.approval_code === null ? '' : row.approval_code;

                        const original_approval_code =
                            row.original_approval_code === null
                                ? ''
                                : `${row.original_approval_code} / `;

                        return original_approval_code + approval_code;
                    },

                    card_type: (h, row) => {
                        return row.card_type === null ? '' : row.card_type;
                    },

                    receipt_type: (h, row) => {
                        return row.receipt_type.toUpperCase();
                    },

                    total_amount(h, row) {
                        return require('../type')(h, row);
                    },

                    cashier(h, row) {
                        if (row.refunded) {
                            return row.refunded_by;
                        }

                        if (row.voided) {
                            return row.voided_by;
                        }

                        return row.cashier;
                    },
                },
                rowClassCallback: row => 'pointer',
                rowClickCallback: row => {
                    this.showReceipt(row.id, row.refund_id, row.voided);
                },
                pdf_url: Laroute.route(
                    'franchise.report.transaction.es.exportAsFile',
                    { type: 'PDF', summary: false },
                ),
                csv_url: Laroute.route(
                    'franchise.report.transaction.es.exportAsFile',
                    { type: 'CSV', summary: true },
                ),
                xls_url: Laroute.route(
                    'franchise.report.transaction.es.exportAsFile',
                    { type: 'XLSX', summary: true },
                ),

                actionButtons: actionButtons,

                columnsClasses: {
                    total_amount: 'text-right',
                    balance: 'text-right',
                    amount: 'text-right',
                    terminal_type: 'text-left',
                },
                footer: false,
            },
            selectedPayments: [],
        };
    },

    computed: {
        ...mapState('tillFilter', ['filters']),

        api() {
            return Laroute.route(
                'franchise.report.transaction.es.transactionList',
            );
        },

        mapPaymentList() {
            let array = this.paymentList;
            let opt = array.filter(e => e.id === 'OPT');

            if (opt.length === 0) {
                array.push({
                    id: 'OPT',
                    type: 1,
                    name: 'OPT',
                });
            }

            return array;
        },
    },

    watch: {
        filters: function(to, from) {
            if (to === from) return;

            this.setFilter();
        },
    },

    mounted() {
        this.getPaymentListIfNecessary();

        this.$watch(
            () => this.$refs.datatable.tableData.length,
            data => {
                this.count = data;
            },
        );

        this.createVuexSubscription(`${TableName}/LOADING`, () => {
            this.isLoading = true;
        });

        this.createVuexSubscription(`${TableName}/LOADED`, () => {
            this.isLoading = false;
        });

        this.createVuexSubscription(`${TableName}/ERROR`, () => {
            this.isLoading = false;
        });
    },

    methods: {
        downloadExportDetails(type) {
            let url = Laroute.route(
                'franchise.report.transaction.es.exportAsFile',
                { type, summary: true },
            );
            window.open(
                `${url}?${this.$refs.datatable.getQueryParams() +
                    '&details=true'}`,
            );
        },

        showReceipt(transaction_id, refund_id = null, voided = false) {
            this.$refs.receipt.$emit(
                'show-receipt',
                transaction_id,
                refund_id,
                voided,
            );
        },

        setPaymentFilter() {
            this.$store.commit(`${this.tableName}/SET_CUSTOM_FILTER`, {
                filter: 'payments_id',
                value: this.selectedPayments.map(p => {
                    return p.name === 'IPT' || p.name === 'OPT' ? p.name : p.id;
                }),
            });
        },

        setFilter() {
            this.$store.commit(`${this.tableName}/SET_CUSTOM_FILTER`, {
                filter: 'filters',
                value: this.filters,
            });
        },

        setLimit(limit) {
            this.$refs.datatable.setLimit(limit);
        },

        setAfterKey(afterKey) {
            this.$store.commit(`${this.tableName}/SET_CUSTOM_FILTER`, {
                filter: 'afterKey',
                value: afterKey,
            });

            this.afterKey = afterKey;
        },

        resetPagination() {
            this.$refs.nav.$emit('reset-es-pagination');
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/transaction/es-report/vuetable-transaction-es.vue